/**
 * @fileoverview Gateway Processing utility for Click&Pledge payments.
 * Simplified implementations of cnpGenerateXMLDOM and cnpGatewayProcess
 * adapted for React/Redux architecture.
 */
import logger from '../../../../app/logger';
import { isDevelopment } from '../../../../base/util/devUtils';
import { resolveCnpAppMode } from '../../../walletPayments';

import { postGatewayProcessing } from './apiActions';
import { XML_ENCRYPTION_KEY, getGatewayConfig } from './constants';

/**
 * Escape special XML characters
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeXml(str) {
    return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

/**
 * Get substring before '_secret' in a string
 * @param {string} str - The string to process
 * @returns {string} The substring before '_secret'
 */
export function getSubstringBeforeSecret(str) {
    const index = str.indexOf('_secret');

    return index === -1 ? str : str.substring(0, index);
}

/**
 * Generate AES key from passphrase using SHA-256
 * This matches the original cnpGetAESKeyFromPassphrase implementation
 * @param {string} passphrase - The passphrase
 * @returns {Promise<ArrayBuffer>} The derived key buffer
 */
async function getAESKeyFromPassphrase(passphrase) {
    const passphraseBytes = new TextEncoder().encode(passphrase);
    const hash = await crypto.subtle.digest('SHA-256', passphraseBytes);

    return hash;
}

/**
 * Encrypt XML data using AES-GCM
 * @param {string} xmlString - The XML string to encrypt
 * @param {string} passphrase - The passphrase for encryption
 * @returns {Promise<Object>} Object containing encryptedData, iv, and authTag
 */
async function encryptXMLProcess(xmlString, passphrase) {
    const encoder = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const keyBuffer = await getAESKeyFromPassphrase(passphrase);
    const key = await crypto.subtle.importKey('raw', keyBuffer, 'AES-GCM', false, [ 'encrypt' ]);

    const encryptedData = await crypto.subtle.encrypt(
        {
            name: 'AES-GCM',
            iv
        },
        key,
        encoder.encode(xmlString)
    );

    const encryptedArray = new Uint8Array(encryptedData);
    const authTag = encryptedArray.slice(encryptedArray.length - 16);
    const ciphertext = encryptedArray.slice(0, encryptedArray.length - 16);

    return {
        encryptedData: Array.from(ciphertext),
        iv: Array.from(iv),
        authTag: Array.from(authTag)
    };
}

/**
 * Parse the gateway XML response
 * @param {string} xmlString - The XML response string
 * @returns {Object} Parsed response object
 */
function parseGatewayXMLResponse(xmlString) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

    return {
        gatewayTransactionNumber: xmlDoc.getElementsByTagName('a:GatewayTransactionNumber')[0]?.textContent || '',
        resultCode: xmlDoc.getElementsByTagName('a:ResultCode')[0]?.textContent || '',
        resultData: xmlDoc.getElementsByTagName('a:ResultData')[0]?.textContent || '',
        transactionNumber: xmlDoc.getElementsByTagName('a:TransactionNumber')[0]?.textContent || '',
        vaultGUID: xmlDoc.getElementsByTagName('a:VaultGUID')[0]?.textContent || '',
        version: xmlDoc.getElementsByTagName('a:Version')[0]?.textContent || ''
    };
}

/**
 * Generate generic Payment XML
 * @param {Object} params - Common parameters
 * @param {string} paymentMethodXML - The specific XML block for the payment method
 * @returns {string} Generated XML string
 */
function generatePaymentXML(params, paymentMethodXML) {
    const {
        cnpPayQuicklyData,
        firstName,
        lastName,
        email,
        amount,
        recurringType = 'One Time Only',
        installments = 999,
        chargeDate
    } = params;
    const forms = cnpPayQuicklyData?.forms || {};
    const formSettings = forms?.FormSettings || {};
    const fallbackFormSettings = params.formSettings || {};
    const version = '1.0.0';
    const accGUID = formSettings?.AccountGUID || fallbackFormSettings?.AccountGUID || params.accountGuid || '';
    const accID = forms?.OrganizationID || '';
    const campaignID = formSettings?.CampaignDetails?.CampaignID
        || fallbackFormSettings?.CampaignDetails?.CampaignID
        || params.formSettings?.CampaignDetails?.CampaignID
        || '';
    const ipAddr = forms?.IPAddress || '';
    const declineFraud = forms?.DeclineFraud || 'true';
    const currentURL = encodeURIComponent(window.location.href);
    const taxDeductible = formSettings?.TaxDeductible || fallbackFormSettings?.TaxDeductible || 'true';

    const totalAmt = Math.round(amount);
    const unitPrice = totalAmt;
    const unitDeductible = 0;
    const itemIDStart = 183000;

    const safeBillingFirstName = escapeXml(firstName);
    const safeBillingLastName = escapeXml(lastName);
    const safeBillingEmail = escapeXml(email);

    let xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:pay="https://ClickAndPledge.com/PaymentService/">
        <soapenv:Header/>
        <soapenv:Body>
            <pay:Operation>
                <pay:instruction>
                <![CDATA[<?xml version="1.0"?>
                <CnPAPI xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns="urn:APISchema.xsd">
                <Version>${version}</Version>
                <Engine>
                    <Application>
                        <Name>MeetHourGivent</Name>
                        <Version>${version}</Version>
                    </Application>
                    <Request>
                        <Operation>
                            <OperationType>Transaction</OperationType>
                            <IPAddress>${ipAddr}</IPAddress>
                            <DeclineFraud>${declineFraud}</DeclineFraud>
                            <UrlReferrer>${currentURL}</UrlReferrer>
                            <TrackingId></TrackingId>
                        </Operation>
                        <Authentication>
                            <AccountGuid>${accGUID}</AccountGuid>
                            <AccountID>${accID}</AccountID>
                        </Authentication>
                        <Order>
                            <OrderMode>Production</OrderMode>
                            <Tracker></Tracker>
                            <CardHolder>
                                <BillingInformation>
                                    <BillingFirstName>${safeBillingFirstName}</BillingFirstName>
                                    <BillingMI></BillingMI>
                                    <BillingLastName>${safeBillingLastName}</BillingLastName>
                                    <BillingEmail>${safeBillingEmail}</BillingEmail>
                                </BillingInformation>
                                ${paymentMethodXML}
                            </CardHolder>
                            <OrderItemList>
                                <OrderItem>
                                    <ItemID>${itemIDStart}</ItemID>
                                    <ItemName>General Donation</ItemName>
                                    <Quantity>1</Quantity>
                                    <UnitPrice>${unitPrice}</UnitPrice>
                                    <UnitDeductible>${unitDeductible}</UnitDeductible>
                                    <SKU></SKU>
                                    <GAU></GAU>
                                </OrderItem>
                            </OrderItemList>
                            <Receipt>
                                <SendReceipt>true</SendReceipt>
                                <Anonymous>false</Anonymous>
                                <Deductible>${taxDeductible}</Deductible>
                            </Receipt>
                            <Transaction>
                                <TransactionType>Payment</TransactionType>`;

    if (recurringType !== 'One Time Only' && installments > 0) {
        xml += `
                                <Recurring>
                                    <Installment>${installments}</Installment>
                                    <Periodicity>${recurringType}</Periodicity>
                                    <RecurringMethod>Subscription</RecurringMethod>
                                </Recurring>`;
    }

    xml += `
                                <CurrentTotals>
                                    <TotalDiscount>0</TotalDiscount>
                                    <TotalTax>0</TotalTax>
                                    <TotalDeductible>${unitDeductible}</TotalDeductible>
                                    <Total>${totalAmt}</Total>
                                </CurrentTotals>`;

    if (chargeDate) {
        xml += `
                                <ChargeDate>${chargeDate}</ChargeDate>`;
    }

    if (campaignID) {
        xml += `
                                <CustomParameters>
                                    <Parameter>
                                        <Field>ConnectCampaignId</Field>
                                        <Value>${campaignID}</Value>
                                    </Parameter>
                                </CustomParameters>`;
    }

    xml += `
                            </Transaction>
                        </Order>
                    </Request>
                </Engine>
                </CnPAPI>]]>
                </pay:instruction>
            </pay:Operation>
        </soapenv:Body>
    </soapenv:Envelope>`;

    return xml;
}

/**
 * Generic Process Payment Gateway
 * @param {Object} params - Parameters
 * @param {string} xmlData - Generated XML
 * @returns {Promise<Object>} Response
 */
async function processGenericGateway(params, xmlData) {
    const { cnpPayQuicklyData } = params;

    try {
        const encryptedXML = await encryptXMLProcess(xmlData, XML_ENCRYPTION_KEY);

        const isDevelopmentApi = isDevelopment();
        const accountMode = cnpPayQuicklyData?.forms?.Mode || 'P';
        const cnpAppMode = resolveCnpAppMode(isDevelopmentApi, accountMode); // LIVE means meethour.io environment

        const config = getGatewayConfig(cnpAppMode);
        const gatewayApiUrl = config.GATEWAY_PROCESS_API;

        const result = await postGatewayProcessing({
            url: gatewayApiUrl,
            payload: encryptedXML
        });

        if (!result || result instanceof Error) {
            const errorMsg = result instanceof Error ? result.message : 'Gateway request failed';

            return {
                success: false,
                error: errorMsg,
                resultCode: '-1',
                resultData: errorMsg
            };
        }

        const parsedResponse = parseGatewayXMLResponse(result);
        const isSuccess = parsedResponse.resultCode === '0';

        return {
            success: isSuccess,
            error: isSuccess ? '' : parsedResponse.resultData || 'Transaction failed',
            transactionNumber: parsedResponse.transactionNumber,
            resultCode: parsedResponse.resultCode,
            resultData: parsedResponse.resultData,
            gatewayTransactionNumber: parsedResponse.gatewayTransactionNumber,
            rawResponse: parsedResponse
        };
    } catch (error) {
        logger.error('Gateway processing error:', error);

        return {
            success: false,
            error: error.message || 'Payment processing failed',
            resultCode: '-1',
            resultData: 'An error occurred while processing your payment'
        };
    }
}

/**
 * Prepare common params and amount conversion
 * @param {Object} params
 * @returns {Object} formatted params
 */
function preparePaymentParams(params) {
    const {
        meetData,
        finalDonationAmt,
        contactOverride,
        chargeDate,
        ...rest
    } = params;

    const firstName = contactOverride?.fname || meetData?.first_name || '';
    const lastName = contactOverride?.lname || meetData?.last_name || '';
    const email = contactOverride?.email || meetData?.email || '';
    const amount = Math.round((parseFloat(finalDonationAmt) || 0) * 100);

    return {
        firstName,
        lastName,
        email,
        amount,
        finalDonationAmt, // keep original if needed
        chargeDate: chargeDate || '',
        meetData,
        ...rest
    };
}

/**
 * Generate XML DOM for Amazon Pay payment
 * @param {Object} params
 * @returns {string} Generated XML string
 */
export function generateXMLForAmazonPay(params) {
    const { paymentIntent } = params;
    const paymentMethodXML = `
        <PaymentMethod>
            <PaymentType>Stripe</PaymentType>
            <Stripe>
                <PaymentIntent>${paymentIntent}</PaymentIntent>
            </Stripe>
        </PaymentMethod>`;

    return generatePaymentXML(params, paymentMethodXML);
}

/**
 * Process Amazon Pay payment
 * @param {Object} params
 * @returns {Promise<Object>}
 */
// eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
export async function processAmazonPayGateway(params) {
    const preparedParams = preparePaymentParams(params);
    const xmlData = generateXMLForAmazonPay(preparedParams);

    return processGenericGateway(preparedParams, xmlData);
}

/**
 * Generate XML DOM for PayPal payment
 * @param {Object} params
 * @returns {string} Generated XML string
 */
export function generateXMLForPayPal(params) {
    const { orderID } = params;
    const paymentMethodXML = `
                                <PaymentMethod>
                                    <PaymentType>PayPal</PaymentType>
                                    <PayPal>
                                        <PayPalOrderId>${orderID}</PayPalOrderId>
                                    </PayPal>
                                </PaymentMethod>`;

    return generatePaymentXML(params, paymentMethodXML);
}

/**
 * Process PayPal payment
 * @param {Object} params
 * @returns {Promise<Object>}
 */
// eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
export async function processPayPalGateway(params) {
    const preparedParams = preparePaymentParams(params);
    const xmlData = generateXMLForPayPal(preparedParams);

    return processGenericGateway(preparedParams, xmlData);
}

/**
 * Process Stripe payment
 * @param {Object} params
 * @returns {Promise<Object>}
 */
// eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
export async function processStripeGateway(params) {
    const { paymentIntent } = params;
    const cleanPaymentIntent = getSubstringBeforeSecret(paymentIntent);

    // Reuse Amazon Pay XML generation as it uses the same Stripe structure
    const preparedParams = preparePaymentParams({ ...params,
        paymentIntent: cleanPaymentIntent });
    const xmlData = generateXMLForAmazonPay(preparedParams);

    return processGenericGateway(preparedParams, xmlData);
}
